import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Plot from "react-plotly.js";
import { assembliesApi } from "../../api/assemblies";
import { bladesApi } from "../../api/blades";
import Button from "../../components/ui/Button";
import { Card, CardHeader, CardBody } from "../../components/ui/Card";
import AssemblyPreviewPanel from "../../components/charts/AssemblyPreviewPanel";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";

import { useChartColors } from "../../chartTheme";

const TABS = ["Blade Rows", "H-Q Curve"];

export default function AssemblyDetailPage() {
  const { ROW_COLORS, COLOR_DARK } = useChartColors();
  const { projectId, assemblyId } = useParams();
  const navigate = useNavigate();
  const [assembly, setAssembly]   = useState(null);
  const [designs, setDesigns]     = useState([]);
  const [err, setErr]             = useState(null);
  const [addingDesignId, setAddingDesignId] = useState("");
  const [editingZOffset, setEditingZOffset] = useState({});
  const [hqInput, setHqInput]     = useState("");
  const [activeTab, setActiveTab] = useState("Blade Rows");
  const [showPreview, setShowPreview]   = useState(true);
  const [previewWidth, setPreviewWidth] = useState(480);

  const isDragging = useRef(false);
  const dragStart  = useRef({ x: 0, width: 0 });

  // Drag-to-resize preview panel
  useEffect(() => {
    const onMouseMove = (e) => {
      if (!isDragging.current) return;
      const delta = dragStart.current.x - e.clientX;
      setPreviewWidth(Math.max(300, Math.min(window.innerWidth * 0.70, dragStart.current.width + delta)));
    };
    const onMouseUp = () => {
      if (!isDragging.current) return;
      isDragging.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, []);

  const onDividerMouseDown = (e) => {
    isDragging.current = true;
    dragStart.current  = { x: e.clientX, width: previewWidth };
    document.body.style.cursor    = "col-resize";
    document.body.style.userSelect = "none";
    e.preventDefault();
  };

  const load = useCallback(() => {
    assembliesApi.get(assemblyId)
      .then(r => setAssembly(r.data))
      .catch(() => setErr("Failed to load assembly"));
  }, [assemblyId]);

  useEffect(() => {
    load();
    bladesApi.list(projectId).then(r => setDesigns(r.data)).catch(() => {});
  }, [load, projectId]);

  const handleAddMember = async () => {
    if (!addingDesignId) return;
    try {
      await assembliesApi.addMember(assemblyId, {
        blade_design_id: parseInt(addingDesignId),
        position: assembly.members.length,
        z_offset: 0,
      });
      load();
      setAddingDesignId("");
    } catch (e) {
      setErr("Failed to add member: " + (e.response?.data?.error || "unknown"));
    }
  };

  const handleZOffsetBlur = async (memberId, value) => {
    const parsed = parseFloat(value);
    if (isNaN(parsed)) return;
    try {
      await assembliesApi.updateMember(assemblyId, memberId, { z_offset: parsed });
      load();
    } catch {
      setErr("Failed to update z-offset");
    }
  };

  const handleMemberToggle = async (memberId, field, value) => {
    try {
      await assembliesApi.updateMember(assemblyId, memberId, { [field]: value });
      load();
    } catch {
      setErr("Failed to update member");
    }
  };

  const handleRemoveMember = async (memberId) => {
    await assembliesApi.removeMember(assemblyId, memberId);
    load();
  };

  const handleAddHQ = async () => {
    try {
      const lines = hqInput.trim().split("\n").map(l => {
        const [q, h, eta] = l.trim().split(/[\s,;]+/).map(Number);
        return { flow_rate: q, head: h, efficiency: eta || undefined, source: "measured" };
      }).filter(p => !isNaN(p.flow_rate) && !isNaN(p.head));
      await assembliesApi.addHQPoints(assemblyId, lines);
      setHqInput("");
      load();
    } catch {
      setErr("Failed to add H-Q points");
    }
  };

  if (!assembly) return <div className="p-6 text-gray-500">Loading…</div>;

  const measuredHQ = assembly.hq_data || [];
  const hqTraces = [
    measuredHQ.length > 0 && {
      x: measuredHQ.map(p => p.flow_rate),
      y: measuredHQ.map(p => p.head),
      mode: "markers", name: "H [m]",
      marker: { color: COLOR_DARK, size: 7 },
    },
    measuredHQ.some(p => p.efficiency != null) && {
      x: measuredHQ.filter(p => p.efficiency != null).map(p => p.flow_rate),
      y: measuredHQ.filter(p => p.efficiency != null).map(p => p.efficiency * 100),
      mode: "markers", name: "η [%]", yaxis: "y2",
      marker: { color: "#f59e0b", size: 7, symbol: "diamond" },
    },
  ].filter(Boolean);

  return (
    <div className="flex-1 flex flex-col overflow-hidden">

      {/* ── Header bar ──────────────────────────────────────────────────── */}
      <div className="bg-white border-b border-gray-200 px-6 py-0 flex items-center gap-3 shrink-0">
        <button
          onClick={() => navigate(`/projects/${projectId}`)}
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 py-3 transition-colors"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Back
        </button>
        <span className="text-gray-300">|</span>
        <span className="text-sm font-semibold text-gray-700 py-3 border-l-2 border-bh-500 pl-3">
          {assembly.name}
        </span>
        <div className="flex gap-3 text-xs text-gray-400">
          {assembly.rpm        && <span>{assembly.rpm} rpm</span>}
          {assembly.design_flow && <span>Q = {assembly.design_flow} m³/s</span>}
          {assembly.design_head && <span>H = {assembly.design_head} m</span>}
        </div>

        {/* Tabs */}
        <nav className="flex gap-0 ml-4">
          {TABS.map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab
                  ? "border-bh-600 text-bh-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              {tab}
            </button>
          ))}
        </nav>

        {activeTab === "Blade Rows" && (
          <button
            onClick={() => setShowPreview(p => !p)}
            className="ml-auto text-xs text-gray-400 hover:text-gray-700 border border-gray-200 rounded px-2.5 py-1 bg-white hover:bg-gray-50 transition-colors shrink-0"
          >
            {showPreview ? "⊡ Hide preview" : "⊞ Preview"}
          </button>
        )}
      </div>

      {err && (
        <div className="px-6 py-2 bg-red-50 border-b border-red-100 text-red-600 text-sm shrink-0">
          {err}
        </div>
      )}

      {/* ── Body ────────────────────────────────────────────────────────── */}
      <div className="flex-1 flex overflow-hidden bg-gray-50">

        {/* Main scrollable content */}
        <div className="flex-1 min-w-0 overflow-y-auto p-6">

          {/* ── Blade Rows tab ── */}
          {activeTab === "Blade Rows" && (
            <Card>
              <CardBody>
                <div className="space-y-2 mb-4">
                  {assembly.members.length === 0 && (
                    <p className="text-gray-500 text-sm">
                      No blade rows yet. Add designs below — they are automatically released when added here.
                    </p>
                  )}
                  {assembly.members.map(m => (
                    <div key={m.id}
                      className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
                      <div className="flex items-center gap-3">
                        <span
                          className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0"
                          style={{ backgroundColor: ROW_COLORS[m.position % ROW_COLORS.length] }}
                        >
                          {m.position + 1}
                        </span>
                        <span className="text-gray-900 text-sm font-medium">
                          {m.blade_design?.name}
                        </span>
                        <span className="text-gray-400 text-xs">
                          {m.blade_design?.d2
                            ? `D₂=${(m.blade_design.d2 * 1000).toFixed(0)} mm` : ""}
                        </span>
                      </div>
                      <div className="flex items-center gap-4">
                        <label className="flex items-center gap-1.5 text-xs text-gray-500">
                          <span>Δz</span>
                          <input
                            type="number"
                            step="1"
                            value={editingZOffset[m.id] ?? m.z_offset ?? 0}
                            onChange={e => setEditingZOffset(s => ({ ...s, [m.id]: e.target.value }))}
                            onBlur={e => handleZOffsetBlur(m.id, e.target.value)}
                            className="w-20 text-right border border-gray-300 rounded px-2 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-bh-500 bg-white"
                          />
                          <span>mm</span>
                        </label>
                        <label className="flex items-center gap-1 text-xs text-gray-500 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={!!m.mirror_x}
                            onChange={e => handleMemberToggle(m.id, "mirror_x", e.target.checked)}
                            className="accent-bh-600 cursor-pointer"
                          />
                          Mirror x
                        </label>
                        <label className="flex items-center gap-1 text-xs text-gray-500 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={!!m.mirror_z}
                            onChange={e => handleMemberToggle(m.id, "mirror_z", e.target.checked)}
                            className="accent-bh-600 cursor-pointer"
                          />
                          Mirror z
                        </label>
                        <button onClick={() => handleRemoveMember(m.id)}
                          className="text-gray-400 hover:text-red-500 text-xs">
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Add member */}
                <div className="flex items-end gap-3 pt-3 border-t border-gray-100">
                  <div className="flex-1">
                    <label className="block text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">
                      Blade design
                    </label>
                    <select value={addingDesignId}
                      onChange={e => setAddingDesignId(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-bh-500">
                      <option value="">Select design…</option>
                      {designs.filter(d => !d.is_released).map(d => (
                        <option key={d.id} value={d.id}>{d.name} v{d.version}</option>
                      ))}
                      {designs.some(d => d.is_released) && (
                        <optgroup label="Released">
                          {designs.filter(d => d.is_released).map(d => (
                            <option key={d.id} value={d.id}>
                              {d.name}
                            </option>
                          ))}
                        </optgroup>
                      )}
                    </select>
                  </div>
                  <Button onClick={handleAddMember} disabled={!addingDesignId} className="shrink-0">
                    Add row
                  </Button>
                </div>
              </CardBody>
            </Card>
          )}

          {/* ── H-Q Curve tab ── */}
          {activeTab === "H-Q Curve" && (
            <div className="space-y-4">
              <Card>
                <CardHeader title="H-Q Curve Data" />
                <CardBody>
                  <p className="text-gray-500 text-xs mb-2">
                    Paste measured points (one per line):{" "}
                    <code className="text-gray-700 bg-gray-100 px-1 rounded">
                      Q[m³/s]  H[m]  η[-]
                    </code>
                  </p>
                  <textarea
                    value={hqInput}
                    onChange={e => setHqInput(e.target.value)}
                    rows={5}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm font-mono mb-3
                      focus:outline-none focus:ring-1 focus:ring-bh-500"
                    placeholder={"0.05  18.2  0.78\n0.10  20.0  0.82\n0.15  19.1  0.79"}
                  />
                  <div className="flex gap-3 items-center">
                    <Button variant="secondary" onClick={handleAddHQ} disabled={!hqInput.trim()}>
                      Add points
                    </Button>
                    <Button variant="ghost"
                      onClick={() => { assembliesApi.clearHQ(assemblyId); load(); }}>
                      Clear all
                    </Button>
                    <span className="text-gray-500 text-sm">
                      {measuredHQ.length} point{measuredHQ.length !== 1 ? "s" : ""} stored
                    </span>
                  </div>
                </CardBody>
              </Card>

              {hqTraces.length > 0 && (
                <Card>
                  <CardHeader title="H-Q Chart" />
                  <CardBody>
                    <Plot
                      data={hqTraces}
                      layout={{
                        autosize: true,
                        margin: { t: 16, r: 60, b: 48, l: 56 },
                        xaxis: { title: "Flow rate Q [m³/s]", zeroline: false },
                        yaxis: { title: "Head H [m]", zeroline: false },
                        yaxis2: {
                          title: "Efficiency η [%]", overlaying: "y",
                          side: "right", zeroline: false,
                        },
                        legend: { orientation: "h", y: -0.22 },
                        paper_bgcolor: "transparent",
                        plot_bgcolor: "#f9fafb",
                        font: { size: 11 },
                      }}
                      config={{ displayModeBar: false, responsive: true }}
                      style={{ width: "100%", height: "360px" }}
                      useResizeHandler
                    />
                  </CardBody>
                </Card>
              )}
            </div>
          )}

        </div>

        {/* ── Preview panel (Blade Rows tab only) ── */}
        {activeTab === "Blade Rows" && showPreview && (
          <>
            <div
              onMouseDown={onDividerMouseDown}
              className="w-1.5 shrink-0 bg-gray-200 hover:bg-bh-400 active:bg-bh-500 cursor-col-resize transition-colors"
            />
            <div
              className="shrink-0 flex flex-col bg-white overflow-hidden border-l border-gray-200"
              style={{ width: previewWidth }}
            >
              <AssemblyPreviewPanel members={assembly.members} />
            </div>
          </>
        )}

      </div>
    </div>
  );
}
